import { replaceEqualDeep } from "@tanstack/react-query";
import { isPlainObject } from "@common/helpers/typeGuards";

const isSameDate = <T>(previous: T, next: T): boolean =>
    previous instanceof Date &&
    next instanceof Date &&
    previous.getTime() === next.getTime();

/**
 * Swaps each Date in `next` that equals the one at the same path in
 * `previous` for the previous instance. Each write replaces a Date with an
 * equal one, so it stays safe when `next` shares objects with other cache
 * entries (`setQueryData` updaters).
 */
function reuseEqualDates<T>(previous: T, next: T): void {
    if (previous === next || Object.isFrozen(next)) {
        return;
    }
    if (Array.isArray(previous) && Array.isArray(next)) {
        next.forEach((item, index) => {
            if (isSameDate(previous[index], item)) {
                next[index] = previous[index];
            } else {
                reuseEqualDates(previous[index], item);
            }
        });
    } else if (isPlainObject(previous) && isPlainObject(next)) {
        for (const key of Object.keys(next)) {
            if (isSameDate(previous[key], next[key])) {
                Object.assign(next, { [key]: previous[key] });
            } else {
                reuseEqualDates(previous[key], next[key]);
            }
        }
    }
}

/**
 * Query structural sharing that also counts equal Dates as unchanged.
 * TanStack's default treats every Date as new, so a refetched list whose
 * schema coerces dates came back as all-new rows and re-rendered every
 * consumer.
 */
export function shareQueryData<T>(previous: T | undefined, next: T): T {
    reuseEqualDates(previous, next);
    return replaceEqualDeep(previous, next);
}
